import { DateCell, PriceCell, RawTextCell, TooltipCell } from '@/components/Table/components/Cell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { COUNTRY_FILTER_OPTIONS, getCountryLabel } from '@/constants/country.constant'
import { camelToWords, numberWithCommas } from '@/utils/stringHelper'
import { OfferControlStatus, OfferQuotationStatus, OfferStage, OfferType } from '@eapproval/offer-shared'
import { createColumnHelper } from '@tanstack/react-table'
import OfferListStatusActionMenu from './OfferListStatusActionMenu'
import { UserEditRight, UserRole } from '@/features/users/constants'

const columnHelper = createColumnHelper()

export const offerListColumnIdToDetail = ({
  id,
  salesUserOptions = [],
  creatorOptions = [],
  teamOptions = [],
  businessUnitOptions = [],
  PMOptions = [],
  costCenterOptions = [],
  GCKOptions = [],
  offerStatusOptions = [],
  closeMainReasonOptions = [],
  closeReasonOptions = [],
  currentUser,
}) => {
  switch (id) {
    case 'action':
      return columnHelper.display({
        id: 'action',
        size: 80,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ row: { original } }) => {
          return (
            <OfferListStatusActionMenu
              offerId={original?._id}
              offerStatus={original?.status}
              salesId={original?.salesId}
              projectName={original?.projectName}
              showCloneButton={true}
              showAdminButtons={
                currentUser.role === UserRole.ADMIN || currentUser?.editRights.includes(UserEditRight.WORKFLOW)
              }
              showNoticeApproverButton={
                original?.nextApproverString !== '' || original?.nextBookingApproverString !== ''
              }
              showActivateButton={original?.status === OfferControlStatus.SUPERSEDED}
              showCreateBookingButton={original?.status === OfferQuotationStatus.QUOTATION_COMPLETED}
              showReviseButton={original?.status === OfferQuotationStatus.QUOTATION_COMPLETED}
              showAddCustomerButton={[
                OfferQuotationStatus.QUOTATION_DRAFT,
                OfferQuotationStatus.QUOTATION_REJECTED,
                OfferQuotationStatus.QUOTATION_SUBMITTED,
                OfferQuotationStatus.QUOTATION_REVIEWED,
                OfferQuotationStatus.QUOTATION_APPROVED,
                OfferQuotationStatus.QUOTATION_COMPLETED,
              ].includes(original?.status)}
              showClosedButton={original?.isClosed == false || original?.isClosed == 'no'}
              showUnclosedButton={
                (original?.isClosed == true || original?.isClosed == 'yes') &&
                (currentUser.role === UserRole.ADMIN || currentUser?.editRights.includes(UserEditRight.WORKFLOW))
              }
            />
          )
        },
      })

    case 'salesResponsible':
      return columnHelper.accessor('salesResponsible', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: salesUserOptions,
        },
      })
    case 'creator':
      return columnHelper.accessor('creator', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: creatorOptions,
        },
      })

    case 'team':
      return columnHelper.accessor('team', {
        cell: RawTextCell,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: teamOptions,
        },
      })
    case 'businessUnit':
      return columnHelper.accessor('businessUnit', {
        cell: RawTextCell,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: businessUnitOptions,
        },
      })
    case 'totalContractPrice':
      return columnHelper.accessor('totalContractPrice', {
        cell: ({ getValue, row: { original } }) => <PriceCell currency={original.currency} getValue={getValue} />,

        meta: {
          filterVariant: FilterType.RANGE,
        },
      })
    case 'updatedAt':
      return columnHelper.accessor('updatedAt', {
        header: () => <span>Last Update</span>,
        cell: DateCell,
        meta: {
          filterVariant: FilterType.DATE,
        },
      })
    case 'createdAt':
      return columnHelper.accessor('createdAt', {
        header: () => <span>Create Date</span>,
        cell: DateCell,
        meta: {
          filterVariant: FilterType.DATE,
        },
      })
    case 'countryOfInstallation':
      return columnHelper.accessor('countryOfInstallation', {
        cell: ({ getValue }) => <TooltipCell text={getCountryLabel(getValue())} />,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: COUNTRY_FILTER_OPTIONS,
        },
      })
    case 'manager':
      return columnHelper.accessor('manager', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: PMOptions,
        },
      })
    case 'projectStartDate':
    case 'projectEndDate':
    case 'offerSubmissionDate':
      return columnHelper.accessor(id, {
        cell: DateCell,
        meta: {
          filterVariant: FilterType.DATE,
        },
      })
    case 'profitCenters':
      return columnHelper.accessor('profitCenters', {
        cell: RawTextCell,
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: costCenterOptions,
        },
      })
    case 'grossMargin':
      return columnHelper.accessor('grossMargin', {
        cell: ({ getValue }) => {
          return <TooltipCell text={`${numberWithCommas(getValue()) || 0}%`} />
        },
        meta: {
          filterVariant: FilterType.RANGE,
        },
      })

    case 'isSiemensStandardTerms':
      return columnHelper.accessor('commercialCheckListIsSiemensStandardTerms', {
        header: () => <span>Siemens Standard Terms</span>,

        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
            { label: 'No (With Legal Waiver)', value: 'noWithLegalWaiver' },
          ],
        },
      })
    case 'GCKCodes':
      return columnHelper.accessor('GCKCodes', {
        header: () => <span>GCK</span>,
        cell: RawTextCell,
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: GCKOptions,
        },
      })
    case 'requirePhysicalSignature':
      return columnHelper.accessor('requirePhysicalSignature', {
        header: () => <span>Physical Signature</span>,

        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: 'Required', value: 'required' },
            { label: 'Not Required', value: 'notRequired' },
          ],
        },
      })

    case 'isClosed':
      return columnHelper.accessor('isClosed', {
        header: () => <span>Closed</span>,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      })
    case 'status':
      return columnHelper.accessor('status', {
        cell: ({ getValue, row: { original } }) => {
          let tooltipValue = getValue()

          if (original?.nextApprover && original?.nextApprover !== '') {
            tooltipValue = `Current Approver is ${original.nextApprover}`
          }
          return <TooltipCell text={getValue()} tooltip={tooltipValue} />
        },
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: offerStatusOptions,
        },
      })
    case 'quotationStatus':
      return columnHelper.accessor('quotationStatus', {
        cell: ({ getValue, row: { original } }) => {
          let tooltipValue = getValue()

          if (original?.nextApprover && original?.nextApprover !== '') {
            tooltipValue = `Current Approver is ${original.nextApprover}`
          }
          return <TooltipCell text={getValue()} tooltip={tooltipValue} />
        },
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: offerStatusOptions,
        },
      })
    case 'isSOP':
      return columnHelper.accessor('isSOP', {
        header: () => <span>Security of Payment</span>,
        cell: ({ getValue }) => <TooltipCell text={getValue() ? 'Yes' : 'No'} />,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ],
        },
      })
    case 'offerCloseMainReason':
      return columnHelper.accessor('offerCloseMainReason', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: closeMainReasonOptions,
        },
      })
    case 'offerCloseDetailedReason':
      return columnHelper.accessor('offerCloseDetailedReason', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: closeReasonOptions,
        },
      })
    case 'bookingBond':
      return columnHelper.accessor('bookingBond', {
        header: () => <span>Bond Completed</span>,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      })
    case 'bookingInsurance':
      return columnHelper.accessor('bookingInsurance', {
        header: () => <span>Insurance Completed</span>,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      })
    case 'bookingEPD':
      return columnHelper.accessor('bookingEPD', {
        header: () => <span>EPD Completed</span>,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      })
    case 'bookingCIC':
      return columnHelper.accessor('bookingCIC', {
        header: () => <span>CIC Completed</span>,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      })
    case 'allSubSystemNewOrRenewal':
      return columnHelper.accessor('allSubSystemNewOrRenewal', {
        header: () => <span>New / Renewal</span>,

        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: 'New', value: 'new' },
            { label: 'Renewal', value: 'renewal' },
            {
              label: 'Renewal Of Discontinued',
              value: 'renewalOfDiscontinued',
            },
          ],
        },
      })

    default:
      return columnHelper.accessor(id, {
        header: () => {
          if (Object.keys(columnIdLabelMapping).includes(id)) {
            return <span>{columnIdLabelMapping[id]}</span>
          }
          return <span>{camelToWords(id)}</span>
        },
        cell: RawTextCell,
      })
  }
}

export const columnIdLabelMapping = {
  LoANumber: 'LoA #',
  equipmentIds: 'Equipment IDs',
  customerReference: 'Customer Ref.',
  bookingCustomerPONumber: 'Customer PO #',
  bookingWBSNumber: 'Booking WBS #',
  SISIC: 'SISIC',
  SONumber: 'SAP Quotation#/SO #',
  bookingBondRemark: 'Bond Remark',
  bookingInsuranceRemark: 'Insurance Remark',
  bookingEPDRemark: 'EPD Remark',
  bookingCICRemark: 'CIC Remark',
  revision: 'Version',
  commercialCheckListDLPPeriod: 'DLP Period (Months)',
}

export const BASE_OFFER_LIST_COLUMNS = [
  'action',
  'salesResponsible',
  'creator',
  'projectName',
  'customer',
  'quotationNumber',
  'revision',
  'team',
  'businessUnit',
  'totalContractPrice',
  'updatedAt',
  'createdAt',
  'countryOfInstallation',
  'engineers',
  'manager',
  'projectStartDate',
  'projectEndDate',
  'endCustomer',
  'profitCenters',
  'grossMargin',
  'offerSubmissionDate',
  'isSiemensStandardTerms',
  'GCKCodes',
  'requirePhysicalSignature',
  'bookingWBSNumber',
  'bookingCustomerPONumber',
  'site',
  'customerReference',
  'isClosed',
  'LoANumber',
  'commercialCheckListDLPPeriod',
  // 'securityOfPayment',
  'isSOP',
  'offerCloseMainReason',
  'offerCloseDetailedReason',
  'equipmentIds',
]

export const QUOTATION_OFFER_LIST_COLUMNS = ['quotationStatus']

export const BOOKING_OFFER_LIST_COLUMNS = [
  'status',
  'SONumber',
  'bookingBond',
  'bookingInsurance',
  'bookingEPD',
  'bookingCIC',
  'bookingBondRemark',
  'bookingInsuranceRemark',
  'bookingEPDRemark',
  'bookingCICRemark',
]

export const MAINTENANCE_OFFER_LIST_COLUMNS = ['allSubSystemNewOrRenewal']

export const getOfferListColumns = ({ offerType, offerStage, ...rest }) => {
  let columnIds = [...BASE_OFFER_LIST_COLUMNS]

  if (offerStage === OfferStage.BOOKING) {
    columnIds.push(...BOOKING_OFFER_LIST_COLUMNS)
  } else {
    columnIds.push(...QUOTATION_OFFER_LIST_COLUMNS)
  }

  if (offerType === OfferType.MAINTENANCE) {
    columnIds.push(...MAINTENANCE_OFFER_LIST_COLUMNS)
  }

  return columnIds.map((id) => offerListColumnIdToDetail({ id, ...rest }))
}
